import { useContext, useEffect, useState } from 'react';
import { cacheProvider } from '@/utils/googleTranslateCacheProvider';
import UserPreferencesContext from '@/context/UserPreferencesContext';

export const useGoogleTranslator = (text: string) => {
  const {
    language: { code: to },
    defaultLanguage,
    googleApiKey,
  } = useContext(UserPreferencesContext);
  const [translation, setTranslation] = useState<string>('');

  const translate = async (value: string) => {
    if (defaultLanguage !== to) {
      const translationCached = await cacheProvider.get(to, text);
      if (!translationCached) {
        const result = await tryGetGoogleTranslation(value);
        if (result) {
          setTranslation(result);
        }
      } else {
        setTranslation(translationCached);
      }
    } else {
      setTranslation(value);
    }
  };

  const tryGetGoogleTranslation = async (
    value: string,
  ): Promise<string | undefined> => {
    try {
      // To avoid to be translated from the same language
      if ( defaultLanguage === to) return text
      const valueTranslated = await fetchGoogleTranslation(value);
      if (valueTranslated) {
        cacheProvider.set(to, value, valueTranslated);
      }
      return valueTranslated;
    } catch (e) {
      return text;
    }
  };

  const fetchGoogleTranslation = async (value: string): Promise<string> => {
    const response = await fetch(
      `https://translation.googleapis.com/language/translate/v2?source=${defaultLanguage}&target=${to}&key=${googleApiKey}&q=${value}&format=text`,
    );
    const { data } = await response.json();

    return data.translations[0].translatedText;
  };

  useEffect(() => {
    translate(text || '');
  }, [to, text]);

  return translation || text;
};
